import { useQuery } from '@tanstack/react-query';
import { schoolBandsApi } from '@/features/reports/api';

export const schoolBandsQueryKeys = {
  all: ['schoolBands'] as const,
  byYear: (year: string) => [...schoolBandsQueryKeys.all, year] as const,
  byYearAndMin: (year: string, min: number) =>
    [...schoolBandsQueryKeys.all, year, 'min', min] as const,
};

/**
 * GET /api/app/school-bands?Year=… — school bands for the selected academic year (no MinSchoolsPerBand).
 */
export function useSchoolBandsQuery(year: string | undefined) {
  const y = year?.trim() ?? '';
  const yearNum = Number(y);
  const enabled = y !== '' && Number.isFinite(yearNum);

  return useQuery({
    queryKey: schoolBandsQueryKeys.byYear(y),
    queryFn: () => schoolBandsApi.getList({ year: yearNum, skipCount: 0, maxResultCount: 100 }),
    enabled,
    staleTime: 60_000,
  });
}

/**
 * GET /api/app/school-bands?Year=…&MinSchoolsPerBand=… — size bands for step 2.
 */
export function useSchoolBandsWithMinQuery(
  year: string | undefined,
  minSchoolsPerBand: number | null | undefined,
  options?: { enabled?: boolean }
) {
  const y = year?.trim() ?? '';
  const yearNum = Number(y);
  const min =
    minSchoolsPerBand != null && Number.isFinite(minSchoolsPerBand) && minSchoolsPerBand > 0
      ? Math.floor(minSchoolsPerBand)
      : null;
  const baseEnabled = y !== '' && Number.isFinite(yearNum) && min != null;
  const enabled = (options?.enabled ?? true) && baseEnabled;

  return useQuery({
    queryKey: schoolBandsQueryKeys.byYearAndMin(y, min ?? 0),
    queryFn: () =>
      schoolBandsApi.getList({
        year: yearNum,
        minSchoolsPerBand: min!,
        skipCount: 0,
        maxResultCount: 100,
      }),
    enabled,
    staleTime: 60_000,
  });
}
